import { useMemo, useState } from "react";
import { FileText, MoreHorizontal, Pencil, Pin, PinOff, Plus, Search, SquarePen, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------
   Conversation list for the assistant. Grouped by when they were last
   touched; pinned ones first. Rename inline, delete with confirmation.
   ------------------------------------------------------------------ */

const dayKey = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  const start = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((start(now) - start(d)) / 86400000);
  if (diff <= 0) return "I dag";
  if (diff === 1) return "I går";
  if (diff < 7) return "Siste 7 dagar";
  if (diff < 30) return "Siste 30 dagar";
  return "Eldre";
};
const ORDER = ["Festa", "I dag", "I går", "Siste 7 dagar", "Siste 30 dagar", "Eldre"];

function Row({ c, active, onSelect, onRename, onDelete, onPin }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(c.title);
  const [confirm, setConfirm] = useState(false);
  const commit = () => {
    setEditing(false);
    const t = val.trim();
    if (t && t !== c.title) onRename(c.id, t);
    else setVal(c.title);
  };
  return (
    <div className={cn("group relative flex items-center rounded-lg", active ? "bg-accent/70" : "hover:bg-accent/40")} data-testid="ai-conversation-row" data-active={active ? "true" : "false"}>
      {active && <span className="absolute left-0 top-1/2 h-4 w-0.5 -translate-y-1/2 rounded-full bg-brand" aria-hidden />}
      {editing ? (
        <input
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setVal(c.title); setEditing(false); } }}
          className="h-9 w-full rounded-lg bg-card px-2.5 text-[13.5px] outline-none ring-2 ring-ring"
          aria-label="Nytt namn på samtalen"
          data-testid="ai-conversation-rename-input"
        />
      ) : (
        <button
          type="button"
          onClick={() => onSelect(c.id)}
          onDoubleClick={() => setEditing(true)}
          className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-lg px-2.5 text-left text-[13.5px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          title={c.title}
          data-testid="ai-conversation-select"
        >
          <span className={cn("min-w-0 flex-1 truncate", active ? "font-medium text-foreground" : "text-foreground/85")}>{c.title}</span>
          {c.has_canvas && <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.75} aria-label="Har dokument" />}
          {c.pinned && <Pin className="h-3 w-3 shrink-0 text-muted-foreground" strokeWidth={1.75} aria-label="Festa" />}
        </button>
      )}
      {!editing && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className={cn("mr-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-opacity hover:bg-card hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", active ? "opacity-100" : "opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100")} aria-label="Meir" data-testid="ai-conversation-menu">
              <MoreHorizontal className="h-4 w-4" strokeWidth={1.75} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="shadow-pop w-48 rounded-xl border-0 p-1.5">
            <DropdownMenuItem className="rounded-lg" onSelect={() => setTimeout(() => setEditing(true), 0)} data-testid="ai-conversation-rename"><Pencil className="mr-2 h-4 w-4" strokeWidth={1.75} /> Gje nytt namn</DropdownMenuItem>
            <DropdownMenuItem className="rounded-lg" onSelect={() => onPin(c.id, !c.pinned)} data-testid="ai-conversation-pin">{c.pinned ? <PinOff className="mr-2 h-4 w-4" strokeWidth={1.75} /> : <Pin className="mr-2 h-4 w-4" strokeWidth={1.75} />} {c.pinned ? "Løys" : "Fest øvst"}</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="rounded-lg text-danger focus:text-danger" onSelect={() => setTimeout(() => setConfirm(true), 0)} data-testid="ai-conversation-delete"><Trash2 className="mr-2 h-4 w-4" strokeWidth={1.75} /> Slett</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="shadow-pop rounded-2xl border-0" data-testid="ai-conversation-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-lg font-medium tracking-[-0.01em]">Slette samtalen?</AlertDialogTitle>
            <AlertDialogDescription>«{c.title}» og eventuelle dokument i canvas blir sletta. Dette kan ikkje angrast.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg" data-testid="ai-conversation-delete-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction className="rounded-lg bg-foreground text-background hover:bg-foreground/90" onClick={() => onDelete(c.id)} data-testid="ai-conversation-delete-confirm">Slett</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function ConversationList({ items, activeId, onNew, onSelect, onRename, onDelete, onPin, className, isMac = true }) {
  const [q, setQ] = useState("");
  const groups = useMemo(() => {
    if (!items) return null;
    const term = q.trim().toLowerCase();
    const list = items.filter((c) => !term || (c.title || "").toLowerCase().includes(term) || (c.preview || "").toLowerCase().includes(term));
    const map = new Map();
    for (const c of list) {
      const k = c.pinned ? "Festa" : dayKey(c.updated_at || c.created_at);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(c);
    }
    return ORDER.filter((k) => map.has(k)).map((k) => [k, map.get(k)]);
  }, [items, q]);

  return (
    <div className={cn("flex h-full min-h-0 flex-col", className)} data-testid="ai-conversation-list">
      <div className="flex items-center justify-between px-3 pt-3">
        <p className="px-1.5 text-[12px] font-medium text-muted-foreground">Samtalar</p>
        <Button variant="ghost" size="sm" className="h-8 gap-1.5 rounded-lg px-2 text-[12.5px]" onClick={onNew} data-testid="ai-new-conversation-button" title={`Ny samtale (${isMac ? "⌘" : "Ctrl"}⇧O)`}>
          <SquarePen className="h-4 w-4" strokeWidth={1.75} /> Ny
        </Button>
      </div>
      <div className="px-3 pb-2 pt-1.5">
        <label className="flex h-8 items-center gap-2 rounded-lg bg-secondary/80 px-2.5 text-muted-foreground focus-within:ring-2 focus-within:ring-ring">
          <Search className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} aria-hidden />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk i samtalar" className="w-full bg-transparent text-[13px] text-foreground outline-none placeholder:text-muted-foreground/70" aria-label="Søk i samtalar" data-testid="ai-conversation-search" />
        </label>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3 scrollbar-thin">
        {groups === null && (
          <div className="space-y-1.5 pt-1" aria-busy="true">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-9 rounded-lg bg-secondary" style={{ width: `${70 + ((i * 13) % 30)}%` }} />)}</div>
        )}
        {groups && groups.length === 0 && (
          <div className="px-2 pt-6 text-center" data-testid="ai-conversation-empty">
            <p className="text-[13px] text-muted-foreground">{q ? "Ingen samtalar passar søket." : "Ingen samtalar enno."}</p>
            {!q && <Button variant="outline" size="sm" className="mt-3 rounded-lg bg-card" onClick={onNew}><Plus className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.75} /> Start ei samtale</Button>}
          </div>
        )}
        {groups && groups.map(([label, rows]) => (
          <div key={label} className="mb-3">
            <p className="mb-1 px-2.5 text-[11.5px] font-medium text-muted-foreground/80">{label}</p>
            <div className="grid gap-0.5">
              {rows.map((c) => <Row key={c.id} c={c} active={c.id === activeId} onSelect={onSelect} onRename={onRename} onDelete={onDelete} onPin={onPin} />)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
